import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const CourseDetailsModal = ({ course, isOpen, onClose }) => {
  if (!isOpen || !course) return null;

  const {
    title,
    category,
    image,
    description,
    duration,
    level,
    mode,
    certification,
    skills,
    curriculum,
    prerequisites,
    nextIntake,
    rating,
    schedule,
  } = course;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-background/80 backdrop-blur-sm"
        onClick={onClose}
      />
      {/* Modal */}
      <div className="relative min-h-screen flex items-center justify-center p-4">
        <div className="relative bg-card rounded-xl shadow-card-lg border border-border w-full max-w-4xl max-h-[90vh] overflow-hidden">
          {/* Header */}
          <div className="sticky top-0 bg-card border-b border-border p-6 flex items-start justify-between">
            <div className="flex-1 pr-4">
              <div className="flex items-center space-x-2 mb-2">
                <span className="text-sm text-primary font-medium">{category}</span>
                <div className="flex items-center space-x-1">
                  <Icon name="Star" size={14} className="text-warning fill-current" />
                  <span className="text-sm font-medium">{rating}</span>
                </div>
              </div>
              <h2 className="text-2xl font-bold text-foreground mb-2">{title}</h2>
              <p className="text-muted-foreground">{description}</p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-muted rounded-lg transition-colors flex-shrink-0"
            >
              <Icon name="X" size={24} />
            </button>
          </div>

          {/* Content */}
          <div className="overflow-y-auto max-h-[calc(90vh-120px)]">
            <div className="p-6">
              {/* Course Image */}
              <div className="w-full h-64 rounded-lg overflow-hidden mb-6">
                <Image
                  src={image}
                  alt={title}
                  className="w-full h-full object-cover"
                />
              </div>

              {/* Quick Stats */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                {/* <div className="bg-muted/50 rounded-lg p-4 text-center">
                  <Icon name="Clock" size={24} className="text-primary mx-auto mb-2" />
                  <p className="font-semibold text-foreground">{duration}</p>
                  <p className="text-sm text-muted-foreground">Duration</p>
                </div> */}
                <div className="bg-muted/50 rounded-lg p-4 text-center">
                  <Icon name="BarChart3" size={24} className="text-primary mx-auto mb-2" />
                  <p className="font-semibold text-foreground">{level}</p>
                  <p className="text-sm text-muted-foreground">Level</p>
                </div>
                <div className="bg-muted/50 rounded-lg p-4 text-center">
                  <Icon name="Monitor" size={24} className="text-primary mx-auto mb-2" />
                  <p className="font-semibold text-foreground">{mode}</p>
                  <p className="text-sm text-muted-foreground">Mode</p>
                </div>
                <div className="bg-muted/50 rounded-lg p-4 text-center">
                  <Icon name="Award" size={24} className="text-primary mx-auto mb-2" />
                  <p className="font-semibold text-foreground">{certification}</p>
                  <p className="text-sm text-muted-foreground">Certificate</p>
                </div>
              </div>

              <div className="grid lg:grid-cols-3 gap-8">
                {/* Main Content */}
                <div className="lg:col-span-2 space-y-8">
                  {/* Course Overview */}
                  <div>
                    <h3 className="text-xl font-semibold text-foreground mb-4 flex items-center">
                      <Icon name="BookOpen" size={20} className="mr-2" />
                      Course Overview
                    </h3>
                    <p className="text-muted-foreground leading-relaxed">
                      {description}
                    </p>
                  </div>

                  {/* Curriculum */}
                  <div>
                    <h3 className="text-xl font-semibold text-foreground mb-4 flex items-center">
                      <Icon name="List" size={20} className="mr-2" />
                      Curriculum
                    </h3>
                    <div className="space-y-3">
                      {curriculum?.map((module, index) => (
                        <div key={index} className="border border-border rounded-lg p-4">
                          <div className="flex items-center justify-between mb-2">
                            <h4 className="font-medium text-foreground">{module.title}</h4>
                            <span className="text-sm text-muted-foreground">{module.duration}</span>
                          </div>
                          <p className="text-sm text-muted-foreground mb-2">{module.description}</p>
                          <div className="flex flex-wrap gap-1">
                            {module.topics?.map((topic, topicIndex) => (
                              <span
                                key={topicIndex}
                                className="bg-primary/10 text-primary px-2 py-1 rounded text-xs"
                              >
                                {topic}
                              </span>
                            ))}
                          </div>
                        </div>
                      )) || (
                        <div className="text-center py-8 text-muted-foreground">
                          <Icon name="BookOpen" size={48} className="mx-auto mb-4 opacity-50" />
                          <p>Detailed curriculum will be shared upon enrollment</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Skills You'll Learn */}
                  <div>
                    <h3 className="text-xl font-semibold text-foreground mb-4 flex items-center">
                      <Icon name="Code" size={20} className="mr-2" />
                      {/* Skills You'll Learn */}
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {skills?.map((skill, index) => (
                        <span
                          key={index}
                          className="bg-accent/10 text-accent px-3 py-2 rounded-lg text-sm font-medium"
                        >
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Sidebar */}
                <div className="space-y-6">
                  {/* Pricing */}
                  <div className="bg-muted/50 rounded-lg p-6">
                    <div className="space-y-3 mb-4">
                      <Button variant="default" fullWidth iconName="UserPlus" iconPosition="left" asChild>
                        <a
                          href="https://karumottiportal.ac.ke/Account/StudentApplication?returnUrl=%2FAppAdmin"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Apply Now
                        </a>
                      </Button>
                      <Button
                        variant="outline"
                        fullWidth
                        iconName="Download"
                        iconPosition="left"
                        onClick={() => {
                          const link = document.createElement('a');
                          link.href = '/assets/files/brochure.pdf';
                          link.download = 'KTTI_Brochure.pdf';
                          document.body.appendChild(link);
                          link.click();
                          document.body.removeChild(link);
                        }}
                      >
                        Download Brochure
                      </Button>
                    </div>
                  </div>

                  {/* Course Info */}
                  <div className="bg-card border border-border rounded-lg p-6">
                    <h4 className="font-semibold text-foreground mb-4">Course Information</h4>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Next Intake:</span>
                        <span className="font-medium">{nextIntake}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Prerequisites:</span>
                        <span className="font-medium">{prerequisites}</span>
                      </div>
                    </div>
                  </div>

                  {/* Schedule */}
                  {schedule && (
                    <div className="bg-card border border-border rounded-lg p-6">
                      <h4 className="font-semibold text-foreground mb-4 flex items-center">
                        <Icon name="Calendar" size={16} className="mr-2" />
                        Schedule
                      </h4>
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Days:</span>
                          <span className="font-medium">{schedule?.days}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Time:</span>
                          <span className="font-medium">{schedule?.time}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Duration:</span>
                          <span className="font-medium">{schedule?.sessionDuration}</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CourseDetailsModal;